import { useEffect, useState } from "react";
import { Grid, Button, Box, Stack, Typography, Theme } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { Canvas3D } from "../components/Canvas3D";
import InfoBox from "../components/InfoBox";
import { FaqTreeType } from "../components/FaqTree";
import SearchStandardDialog from "../components/SearchStandardDialog";
import PageContent from "../components/PageContent";
import { useIsMobile } from "../hooks";
import ArrowPreviousMapCarousel from "../components/icons/ArrowPreviousMapCarousel";
import NestedFaqTree, { type HoverData } from "../components/NestedFaqTree";
import { useTesting } from "../hooks";
import ArrowNextMapCarousel from "../components/icons/ArrowNextMapCarousel";
import { useTranslation } from "react-i18next";
import ImageGallery from "react-image-gallery";


type TestTechProps = {
    content: "test" | "tech"
}


export default function TestingTechnologyPage({ content }: TestTechProps) {
    const { isMobile } = useIsMobile();
    const { result } = useTesting(content);
    let navigate = useNavigate();
    const { t } = useTranslation();
    const [openSearchStandard, setOpenSearchStandard] = useState(false);
    const [hoverData, setHoverData] = useState<HoverData>({images: undefined, caption: undefined});
    
    useEffect(() => {
        // All'apertura della pagina, setto le prime immagini e caption disponibili
        if (result && result.length > 0) {
            const newHoverData: HoverData = {images: undefined, caption: undefined};
            for (let i = 0; i < result.length; i++) {
                const iResult = result[i];
                if (iResult.images != null && iResult.images.length > 0 && iResult.caption != null) {
                    newHoverData.images = iResult.images;
                    newHoverData.caption = iResult.caption;
                    setHoverData(newHoverData);
                    break;
                }
            }
        }
    }, [result]);

    return (
        <PageContent>
            {/* Solo per testing */}
            <SearchStandardDialog
                open={openSearchStandard}
                onSubmit={(standardQuery) => {
                    setOpenSearchStandard(false);
                    if (standardQuery !== "") {
                        navigate(`search-standard/${standardQuery}`);
                    }
                }}
                handleClose={() => setOpenSearchStandard(false)}
            />

            <Grid container spacing={isMobile ? 4 : 10}>
                <Grid item md={4} sm={12} xs={12} order={{ md: 1, sm: 2 }}>
                    {
                        isMobile ?
                        <Grid container>
                            <Grid item sm={5} xs={5} sx={{ alignContent: "center", paddingLeft: "0px" }} order={content === "test" ? 1 : 2}>
                                {
                                    content === "test" ?
                                    <Button
                                        sx={{
                                            left: "-20px",
                                            fontSize: "14px",
                                            color: "white",
                                            background: "#37314A",
                                        }}
                                        size="small"
                                        startIcon={
                                            <ArrowPreviousMapCarousel sx={{ color: "software.main" }} />
                                        }
                                        onClick={() => navigate("/software")}
                                    >
                                        CONNECTIVE ENGINEERING
                                    </Button>
                                    :
                                    <Button
                                        sx={{
                                            left: "20px",
                                            fontSize: "14px",
                                            color: "white",
                                            background: "#37314A",
                                        }}
                                        size="small"
                                        endIcon={
                                            <ArrowNextMapCarousel sx={{ color: "testing.main" }} />
                                        }
                                        onClick={() => navigate("/testing")}
                                    >
                                        TIC - TESTING, INSPECTION & CERTIFICATION
                                    </Button>
                                }
                            </Grid>
                            <Grid item sm={7} xs={7} sx={content === "tech" ? { left: "-20px"} : {}} order={content === "test" ? 2 : 1}>
                                {
                                    content === "test" ?
                                    <Canvas3D height={"200px"} width={"auto"}  model="testing.gltf" texture="baked2.webp" isMobile={isMobile}/>
                                    :
                                    <Canvas3D height={"200px"} width={"auto"}  model="technology.gltf" texture="baked.webp" isMobile={isMobile}/>
                                }
                            </Grid>
                        </Grid>
                        :
                        <Stack height={"100%"} justifyContent={"space-between"}>
                            {
                                <Stack spacing={2}>
                                    {
                                        hoverData.images != null && hoverData.images.length > 0 && <Box sx={(theme: Theme) => {return {filter: `drop-shadow(-12px 12px ${theme.palette.secondary.main})`}}}><ImageGallery
                                        autoPlay
                                        slideDuration={600}
                                        slideInterval={4000}
                                        showPlayButton={hoverData.images.length > 1}
                                        showThumbnails={false}
                                        showFullscreenButton={false}
                                        showNav={false}
                                        showBullets={hoverData.images.length > 1}
                                        items={hoverData.images.map(img => {return {
                                            original: "/backend/static/testing/" + img}})}
                                        renderItem={item => <img width={"100%"} src={item.original} style={{borderRadius: "0px"}}/>}
                                        /></Box>
                                    }
                                    <Box textAlign={"left"}>
                                        <Typography>{hoverData.caption}</Typography>
                                    </Box>
                                </Stack>
                            }
                            <InfoBox />
                        </Stack>
                    }
                </Grid>

                <Grid item md={8} sm={12} xs={12} order={{ md: 2, sm: 1 }}>
                        <NestedFaqTree
                            type={content === "test" ? FaqTreeType.Testing : FaqTreeType.Software}
                            tree={result || []}
                            onSearchForStandard={() => setOpenSearchStandard(true)}
                            onHoverCategory={(images: string[] | undefined, caption: string | undefined) => setHoverData({caption: caption, images: images})}
                        />
                </Grid>
                
                {isMobile && (
                    <Grid
                        item
                        md={8}
                        sm={12}
                        xs={12}
                        sx={{ marginTop: "40px" }}
                    >
                        <InfoBox />
                    </Grid>
                )}
            </Grid>
        </PageContent>
    );
};
